import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  TableCellsIcon,
  DocumentIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import {
  computeFiscalPreview,
  EditableConceptLine,
  extractedToLines,
  formatConfidencePercent,
  linesToExtracted,
  LineTipo,
} from "../utils/pdfImportLines";
import {
  buildReviewUx,
  mapApiErrorMessage,
  friendlyConfirmError,
  formatImportDisplayName,
} from "../utils/invoiceImportUx";

export interface PdfImportConcepto {
  descripcion: string;
  importe: number;
  codigo?: string | null;
  cantidad?: number | null;
  unidad?: string | null;
}

export interface PdfImportExtracted {
  numero?: string | null;
  fecha?: string | null;
  cliente?: string | null;
  cif?: string | null;
  direccion_cliente?: string | null;
  base_imponible?: number;
  base_trabajo?: number;
  base_gastos_no_suplidos?: number;
  iva_porcentaje?: number;
  iva_importe?: number;
  irpf_porcentaje?: number;
  irpf_importe?: number;
  total?: number;
  estado?: string;
  confidence?: number;
  revision_requerida?: boolean;
  observacion?: string | null;
  observaciones?: string | null;
  forma_pago?: string | null;
  vencimiento?: string | null;
  /** Texto del PDF donde aparece la fecha (verificación backend) */
  _fechaEvidencia?: string[];
  _fechaMismatch?: boolean;
  _fechaAmbiguous?: boolean;
  tipo_documento?: string;
  conceptos_servicio?: PdfImportConcepto[];
  conceptos?: PdfImportConcepto[];
  gastos_no_suplidos?: { descripcion: string; importe: number; tipo?: string }[];
  suplidos?: { descripcion: string; importe: number }[];
  /** Total suplidos (cálculo interno en revisión de importación) */
  _suplidos_total?: number;
}

export type ImportValidationStatus = "perfect" | "needs_review" | "failed";

export interface ImportValidationError {
  field: string;
  message: string;
}

export interface DocumentImportResult {
  file: string;
  success: boolean;
  invoiceId?: string | null;
  invoiceNumber?: string;
  errors?: string[];
  needsReview?: boolean;
  validationStatus?: ImportValidationStatus;
  validationErrors?: ImportValidationError[];
  fieldFailures?: string[];
  canAutoImport?: boolean;
  extracted?: PdfImportExtracted | null;
  documentType?: string;
  documentKind?: string;
  displayName?: string;
  reviewMessage?: string;
  reviewReason?: string;
}

export type PdfImportResult = DocumentImportResult;

type QueueStatus = "queued" | "analyzing" | "ready" | "needs_review" | "error" | "imported";
type ProcessingStep = "uploading" | "extracting" | "structuring" | "done";

interface QueueItem {
  id: string;
  file: File;
  status: QueueStatus;
  processingStep?: ProcessingStep;
  result?: DocumentImportResult;
  error?: string;
  editLines?: EditableConceptLine[];
}

const PROCESS_STEPS: { key: ProcessingStep; label: string }[] = [
  { key: "uploading", label: "Subiendo" },
  { key: "extracting", label: "Extrayendo" },
  { key: "structuring", label: "Estructurando" },
];

function ProcessingAnimation({ step }: { step: ProcessingStep }) {
  const idx = PROCESS_STEPS.findIndex((s) => s.key === step);
  return (
    <div className="space-y-3 py-2">
      {PROCESS_STEPS.map((s, i) => {
        const active = i === idx;
        const done = i < idx || step === "done";
        return (
          <motion.div
            key={s.key}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: active || done ? 1 : 0.35 }}
            className="flex items-center gap-3 text-sm"
          >
            <span
              className={`h-2 w-2 rounded-full shrink-0 ${
                done ? "bg-emerald-500" : active ? "bg-violet-500 animate-pulse" : "bg-zinc-300"
              }`}
            />
            <span className={active ? "font-medium text-violet-700 dark:text-violet-300" : "text-zinc-500"}>
              {s.label}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
}

interface ImportDocumentsDropzoneProps {
  onImported?: (results: DocumentImportResult[]) => void;
  autoImport?: boolean;
}

const ACCEPTED_EXTENSIONS = new Set([
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "webp",
  "xls",
  "xlsx",
  "csv",
  "doc",
  "docx",
  "txt",
  "odt",
]);

const ACCEPT_ATTR =
  ".pdf,.jpg,.jpeg,.png,.webp,.xls,.xlsx,.csv,.doc,.docx,.txt,.odt,application/pdf,image/jpeg,image/png,image/webp,text/csv,text/plain";

const BATCH_SIZE = 3;
const BATCH_PAUSE_MS = 2500;
const MAX_PREVIEW_ATTEMPTS = 3;
const RETRY_BACKOFF_BASE_MS = 1200;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function indexResultsByFile(results: DocumentImportResult[]): Map<string, DocumentImportResult> {
  const map = new Map<string, DocumentImportResult>();
  for (const r of results) {
    if (r?.file) map.set(r.file, r);
  }
  return map;
}

function resultForItem(
  item: QueueItem,
  results: DocumentImportResult[],
  index: number
): DocumentImportResult {
  const byFile = indexResultsByFile(results);
  return (
    byFile.get(item.file.name) ||
    results[index] || {
      file: item.file.name,
      success: false,
      errors: ["Sin respuesta del servidor para este archivo"],
    }
  );
}

function isAcceptedImportFile(file: File) {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (ACCEPTED_EXTENSIONS.has(ext)) return true;
  if (file.type === "application/pdf") return true;
  if (file.type.startsWith("image/")) return true;
  if (file.type.includes("csv") || file.type.includes("spreadsheet") || file.type.includes("excel")) {
    return true;
  }
  if (file.type.includes("word") || file.type.includes("document")) return true;
  return false;
}

function getFileTypeMeta(file: File): {
  Icon: React.ComponentType<{ className?: string }>;
  color: string;
  label: string;
} {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf") return { Icon: DocumentTextIcon, color: "text-red-500", label: "PDF" };
  if (["jpg", "jpeg", "png", "webp"].includes(ext)) {
    return { Icon: PhotoIcon, color: "text-sky-500", label: "Imagen" };
  }
  if (["xls", "xlsx"].includes(ext)) {
    return { Icon: TableCellsIcon, color: "text-emerald-600", label: "Excel" };
  }
  if (ext === "csv") return { Icon: TableCellsIcon, color: "text-teal-600", label: "CSV" };
  if (["doc", "docx"].includes(ext)) {
    return { Icon: DocumentIcon, color: "text-blue-600", label: "Word" };
  }
  if (ext === "txt") return { Icon: DocumentTextIcon, color: "text-zinc-500", label: "TXT" };
  if (ext === "odt") return { Icon: DocumentIcon, color: "text-orange-600", label: "ODT" };
  return { Icon: DocumentTextIcon, color: "text-violet-500", label: "Documento" };
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

const TIPO_LABELS: Record<LineTipo, string> = {
  servicio: "Servicio / concepto",
  gasto_no_suplido: "Gasto no suplido",
  suplido: "Suplido",
};

interface ImportRunSummary {
  total: number;
  analyzed: number;
  imported: DocumentImportResult[];
  needsReview: { displayName: string; file: string; reason: string }[];
  fail: { displayName: string; file: string; reason: string }[];
}


function isRunIncomplete(item: QueueItem, runIds: Set<string>): boolean {
  if (!runIds.has(item.id)) return false;
  if (item.status === "queued" || item.status === "analyzing") return true;
  if (item.status === "error" && !item.result?.extracted) return true;
  return false;
}

function canAutoImportResult(result: DocumentImportResult): boolean {
  return result.canAutoImport === true || result.validationStatus === "perfect";
}
export const ImportDocumentsDropzone: React.FC<ImportDocumentsDropzoneProps> = ({
  onImported,
  autoImport = false,
}) => {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [batchAutoMode, setBatchAutoMode] = useState(autoImport);
  const [isBusy, setIsBusy] = useState(false);
  const [runTotal, setRunTotal] = useState(0);
  const [batchMeta, setBatchMeta] = useState<{
    current: number;
    total: number;
    pauseRemaining: number | null;
  } | null>(null);
  const [retryMeta, setRetryMeta] = useState<{
    current: number;
    total: number;
    fileName: string;
    attempt: number;
  } | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [runSummary, setRunSummary] = useState<ImportRunSummary | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const processingRef = useRef(false);
  const confirmingRef = useRef(false);
  const runIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setBatchAutoMode(autoImport);
  }, [autoImport]);

  useEffect(() => {
    if (!selectedId && queue.length) {
      const first =
        queue.find(
          (q) =>
            q.status === "ready" || q.status === "analyzing" || q.status === "needs_review"
        ) || queue[0];
      if (first) setSelectedId(first.id);
    }
  }, [queue, selectedId]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const accepted = Array.from(files).filter(isAcceptedImportFile);
    if (!accepted.length) {
      alert("Formato no soportado. Use PDF, imágenes, Excel, CSV, Word (.docx), TXT u ODT.");
      return;
    }

    const tooBig = accepted.filter((f) => f.size > 10 * 1024 * 1024);
    if (tooBig.length) {
      alert(`Máximo 10 MB por archivo: ${tooBig.map((f) => f.name).join(", ")}`);
    }

    const valid = accepted.filter((f) => f.size <= 10 * 1024 * 1024);
    const newItems: QueueItem[] = valid.map((file) => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      file,
      status: "queued",
    }));
    setQueue((prev) => [...prev, ...newItems]);
  }, []);

  const updateItem = (id: string, patch: Partial<QueueItem>) => {
    setQueue((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const runProcessingSteps = async (itemId: string) => {
    const steps: ProcessingStep[] = ["uploading", "extracting", "structuring"];
    for (const step of steps) {
      updateItem(itemId, { processingStep: step });
      await new Promise((r) => setTimeout(r, 380));
    }
  };

  const resolveQueueStatus = (result: DocumentImportResult): QueueStatus => {
    if (!result.extracted) return "error";
    if (result.canAutoImport === true || result.validationStatus === "perfect") {
      return "ready";
    }
    if (
      result.extracted &&
      (result.validationStatus === "needs_review" || result.validationStatus === "failed")
    ) {
      return "needs_review";
    }
    return "needs_review";
  };

  const applyFriendlyMessages = (
    item: QueueItem,
    result: DocumentImportResult | null | undefined,
    opts?: { hardFailure?: boolean; apiMessage?: string }
  ) => {
    const ux = buildReviewUx(item.file.name, result || undefined, {
      hardFailure: opts?.hardFailure,
      apiMessage: opts?.apiMessage,
    });
    return {
      errorTitle: ux.title,
      errorSubtitle: ux.subtitle,
      error: ux.subtitle ? `${ux.title} — ${ux.subtitle}` : ux.title,
      isHardFailure: ux.isHardFailure,
    };
  };

  const applyPreviewResults = (items: QueueItem[], results: DocumentImportResult[]) => {
    const autoImportTargets: { item: QueueItem; result: DocumentImportResult }[] = [];
    let firstReviewId: string | null = null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const result = resultForItem(item, results, i);
      const editLines = result.extracted ? extractedToLines(result.extracted) : [];
      const status = resolveQueueStatus(result);
      const friendly =
        status === "error" || status === "needs_review"
          ? applyFriendlyMessages(item, result, { hardFailure: status === "error" })
          : null;
      updateItem(item.id, {
        status,
        processingStep: "done",
        result,
        editLines,
        error: friendly?.error,
      });
      if (
        result.extracted &&
        (result.canAutoImport === true || result.validationStatus === "perfect")
      ) {
        autoImportTargets.push({
          item: { ...item, result, editLines },
          result,
        });
      }
      if (result.extracted && !firstReviewId) firstReviewId = item.id;
    }
    if (firstReviewId) {
      setSelectedId((prev) => prev || firstReviewId);
    }
    return autoImportTargets;
  };

  const previewSingleItem = async (item: QueueItem): Promise<DocumentImportResult> => {
    updateItem(item.id, { status: "analyzing", processingStep: "uploading" });
    const form = new FormData();
    form.append("files", item.file);
    await runProcessingSteps(item.id);
    const res = await apiService.previewDocumentImport(form);
    if (res.error || !res.data) {
      const apiMsg = mapApiErrorMessage(item.file.name, res.message, res.error);
      updateItem(item.id, {
        status: "error",
        processingStep: "done",
        error: apiMsg,
      });
      return {
        file: item.file.name,
        success: false,
        errors: [apiMsg],
      };
    }
    const results = (res.data as { results: DocumentImportResult[] }).results || [];
    return resultForItem(item, results, 0);
  };

  const previewItems = async (
    items: QueueItem[]
  ): Promise<{
    failed: QueueItem[];
    ready: { item: QueueItem; result: DocumentImportResult }[];
  }> => {
    for (const item of items) {
      updateItem(item.id, { status: "analyzing", processingStep: "uploading" });
    }

    const form = new FormData();
    items.forEach((item) => form.append("files", item.file));

    const stepPromises = items.map((item) => runProcessingSteps(item.id));
    const resPromise = apiService.previewDocumentImport(form);
    const [, res] = await Promise.all([Promise.all(stepPromises), resPromise]);

    if (res.error || !res.data) {
      const errMsg = res.message || res.error || "";
      const isTimeout =
        /timeout|tardó|504|503|502|disponible/i.test(errMsg) || items.length > 1;
      if (isTimeout) {
        const ready: { item: QueueItem; result: DocumentImportResult }[] = [];
        const failed: QueueItem[] = [];
        for (const item of items) {
          const one = await previewSingleItem(item);
          if (one.extracted) {
            const editLines = extractedToLines(one.extracted);
            const status = resolveQueueStatus(one);
            updateItem(item.id, {
              status,
              processingStep: "done",
              result: one,
              editLines,
              error:
                status === "needs_review" ? applyFriendlyMessages(item, one).error : undefined,
            });
            if (
              one.canAutoImport === true ||
              one.validationStatus === "perfect"
            ) {
              ready.push({ item: { ...item, result: one, editLines }, result: one });
            }
          } else {
            failed.push(item);
          }
        }
        return { failed, ready };
      }
      for (const item of items) {
        const apiMsg = mapApiErrorMessage(item.file.name, res.message, res.error);
        updateItem(item.id, {
          status: "error",
          processingStep: "done",
          error: apiMsg,
        });
      }
      return { failed: items, ready: [] };
    }

    const results = (res.data as { results: DocumentImportResult[] }).results || [];
    const ready = applyPreviewResults(items, results);
    const failed = items.filter((item, i) => {
      const r = resultForItem(item, results, i);
      return !r?.extracted;
    });
    return { failed, ready };
  };

  const finishImportRun = (runIds: Set<string>) => {
    setQueue((prev) => {
      const inRun = (q: QueueItem) => runIds.has(q.id);
      const runItems = prev.filter(inRun);
      const finalized = runItems.map((q) => {
        if (!isRunIncomplete(q, runIds)) return q;
        const ux = buildReviewUx(q.file.name, q.result, { hardFailure: true });
        return {
          ...q,
          status: "error" as const,
          processingStep: "done" as const,
          error: ux.subtitle
            ? `${ux.title} — ${ux.subtitle}`
            : "No se pudo analizar tras varios intentos",
        };
      });

      const analyzed = finalized.filter(
        (q) => q.status !== "queued" && q.status !== "analyzing"
      ).length;
      const imported = finalized
        .filter((q) => q.status === "imported" && q.result)
        .map((q) => q.result!);
      const needsReview = finalized
        .filter((q) => q.status === "needs_review")
        .map((q) => {
          const ux = buildReviewUx(q.file.name, q.result);
          return {
            file: q.file.name,
            displayName: formatImportDisplayName(
              q.file.name,
              q.result?.extracted,
              q.result?.invoiceNumber
            ),
            reason:
              q.result?.reviewMessage ||
              (ux.subtitle ? `${ux.title} — ${ux.subtitle}` : ux.title) ||
              q.result?.reviewReason ||
              "revisar datos antes de importar",
          };
        });
      const fail = finalized
        .filter((q) => q.status === "error")
        .map((q) => {
          const ux = buildReviewUx(q.file.name, q.result, { hardFailure: true });
          return {
            file: q.file.name,
            displayName: formatImportDisplayName(q.file.name),
            reason: ux.subtitle || q.error || "no se pudo leer el documento",
          };
        });

      setRunSummary({
        total: finalized.length,
        analyzed,
        imported,
        needsReview,
        fail,
      });
      setShowSummary(true);
      if (imported.length && onImported) onImported(imported);
      return finalized;
    });
  };


  const drainIncompleteRun = async (runIds: Set<string>) => {
    for (let attempt = 1; attempt <= MAX_PREVIEW_ATTEMPTS; attempt++) {
      let stuck: QueueItem[] = [];
      setQueue((prev) => {
        stuck = prev.filter((q) => isRunIncomplete(q, runIds));
        return prev;
      });
      if (!stuck.length) break;

      if (attempt > 1) {
        await sleep(RETRY_BACKOFF_BASE_MS * attempt);
      }

      for (let i = 0; i < stuck.length; i++) {
        const item = stuck[i];
        setRetryMeta({
          current: i + 1,
          total: stuck.length,
          fileName: item.file.name,
          attempt,
        });
        if (item.status === "queued" || item.status === "analyzing") {
          updateItem(item.id, { status: "queued", processingStep: undefined, error: undefined });
        }
        const one = await previewSingleItem(item);
        if (one.extracted) {
          const editLines = extractedToLines(one.extracted);
          const status = resolveQueueStatus(one);
          const friendly =
            status === "needs_review"
              ? applyFriendlyMessages(item, one)
              : null;
          updateItem(item.id, {
            status,
            processingStep: "done",
            result: one,
            editLines,
            error: friendly?.error,
          });
          if (batchAutoMode && canAutoImportResult(one)) {
            await confirmImport(
              [{ item: { ...item, result: one, editLines }, result: one }],
              { silent: true }
            );
          }
        }
      }
    }

    // Pasada final: forzar uno a uno cualquier resto en cola o analizando
    let finalStuck: QueueItem[] = [];
    setQueue((prev) => {
      finalStuck = prev.filter(
        (q) => runIds.has(q.id) && (q.status === "queued" || q.status === "analyzing")
      );
      return prev;
    });
    for (let i = 0; i < finalStuck.length; i++) {
      const item = finalStuck[i];
      setRetryMeta({
        current: i + 1,
        total: finalStuck.length,
        fileName: item.file.name,
        attempt: MAX_PREVIEW_ATTEMPTS,
      });
      updateItem(item.id, { status: "queued", processingStep: undefined });
      const one = await previewSingleItem(item);
      if (one.extracted) {
        const editLines = extractedToLines(one.extracted);
        const status = resolveQueueStatus(one);
        updateItem(item.id, {
          status,
          processingStep: "done",
          result: one,
          editLines,
          error:
            status === "needs_review" ? applyFriendlyMessages(item, one).error : undefined,
        });
        if (batchAutoMode && canAutoImportResult(one)) {
          await confirmImport(
            [{ item: { ...item, result: one, editLines }, result: one }],
            { silent: true }
          );
        }
      }
    }
    setRetryMeta(null);
  };

  const runAutomaticImport = async () => {
    if (processingRef.current) return;

    let pending: QueueItem[] = [];
    setQueue((prev) => {
      pending = prev.filter((q) => q.status === "queued");
      return prev;
    });
    if (!pending.length) return;

    processingRef.current = true;
    setIsBusy(true);
    setShowSummary(false);
    setRunSummary(null);
    const runIds = new Set(pending.map((p) => p.id));
    runIdsRef.current = runIds;
    setRunTotal(pending.length);

    const batches: QueueItem[][] = [];
    for (let i = 0; i < pending.length; i += BATCH_SIZE) {
      batches.push(pending.slice(i, i + BATCH_SIZE));
    }

    try {
      for (let b = 0; b < batches.length; b++) {
        setBatchMeta({ current: b + 1, total: batches.length, pauseRemaining: null });

        let { failed, ready } = await previewItems(batches[b]);
        if (failed.length) {
          for (const item of failed) {
            const one = await previewSingleItem(item);
            if (one.extracted) {
              const editLines = extractedToLines(one.extracted);
              const status = resolveQueueStatus(one);
              updateItem(item.id, {
                status,
                processingStep: "done",
                result: one,
                editLines,
                error:
                  status === "needs_review" ? applyFriendlyMessages(item, one).error : undefined,
              });
              if (one.canAutoImport || one.validationStatus === "perfect") {
                ready.push({ item: { ...item, result: one, editLines }, result: one });
              }
            }
          }
        }

        if (batchAutoMode && ready.length) {
          const perfectOnly = ready.filter(
            ({ result }) =>
              result?.canAutoImport === true || result?.validationStatus === "perfect"
          );
          if (perfectOnly.length) {
            await confirmImport(perfectOnly, { silent: true });
          }
        }

        if (b < batches.length - 1) {
          const pauseSteps = Math.ceil(BATCH_PAUSE_MS / 1000);
          for (let s = pauseSteps; s >= 1; s--) {
            setBatchMeta({ current: b + 1, total: batches.length, pauseRemaining: s });
            await sleep(1000);
          }
          setBatchMeta({ current: b + 1, total: batches.length, pauseRemaining: null });
        }
      }
    } finally {
      setBatchMeta(null);
      try {
        await drainIncompleteRun(runIds);
      } finally {
        setRetryMeta(null);
        setIsBusy(false);
        processingRef.current = false;
        finishImportRun(runIds);
      }
    }
  };

  const processQueue = async () => {
    await runAutomaticImport();
  };

  // La importación automática solo arranca al pulsar «Analizar» (processQueue), no al soltar archivos.

  const updateExtracted = (itemId: string, field: keyof PdfImportExtracted, value: unknown) => {
    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== itemId || !item.result?.extracted) return item;
        return {
          ...item,
          result: {
            ...item.result,
            extracted: { ...item.result.extracted, [field]: value },
          },
        };
      })
    );
  };

  const updateLines = (itemId: string, lines: EditableConceptLine[]) => {
    setQueue((prev) =>
      prev.map((item) => {
        if (item.id !== itemId || !item.result?.extracted) return item;
        const extracted = linesToExtracted(lines, item.result.extracted);
        return {
          ...item,
          editLines: lines,
          result: { ...item.result, extracted },
        };
      })
    );
  };

  const confirmImport = async (
    targets?: { item: QueueItem; result?: DocumentImportResult }[],
    opts?: { silent?: boolean }
  ): Promise<ImportRunSummary> => {
    if (confirmingRef.current) return { total: 0, analyzed: 0, imported: [], needsReview: [], fail: [] };

    const ready =
      targets ||
      queue
        .filter((q) => q.status === "ready" && q.result?.extracted)
        .map((item) => ({ item, result: item.result }));

    if (!ready.length) return { total: 0, analyzed: 0, imported: [], needsReview: [], fail: [] };

    confirmingRef.current = true;
    if (!opts?.silent) setIsBusy(true);
    const ok: DocumentImportResult[] = [];
    const fail: ImportRunSummary["fail"] = [];
    try {
    const payload = {
      invoices: ready.map(({ item, result }) => {
        const ext = item.result?.extracted || result?.extracted;
        const lines = item.editLines || (ext ? extractedToLines(ext) : []);
        const extracted = ext ? linesToExtracted(lines, ext) : ext;
        return {
          file: item.file.name,
          extracted,
        };
      }),
    };

    const res = await apiService.confirmDocumentImport(payload);
    if (res.error || !res.data) {
      ready.forEach(({ item }) => {
        const msg = mapApiErrorMessage(
          item.file.name,
          res.message,
          res.error || "No se pudo guardar; revisa los datos"
        );
        updateItem(item.id, { status: "needs_review", error: msg });
        fail.push({
          file: item.file.name,
          displayName: formatImportDisplayName(item.file.name, item.result?.extracted),
          reason: msg,
        });
      });
      return { total: 0, analyzed: 0, imported: [], needsReview: [], fail };
    }
    const body = res.data as {
      results?: DocumentImportResult[];
      imported?: number;
      message?: string;
    } | undefined;
    const results = body?.results || [];
    const byFile = indexResultsByFile(results);

    ready.forEach(({ item }, i) => {
      const saved = byFile.get(item.file.name) || results[i];
      if (saved?.success) {
        updateItem(item.id, { status: "imported", result: saved, error: undefined });
        ok.push(saved);
      } else {
        const friendly = friendlyConfirmError(
          item.file.name,
          saved || item.result,
          body?.message || res.message
        );
        updateItem(item.id, {
          status: saved?.extracted ? "needs_review" : "error",
          error: friendly,
          result: saved || item.result,
        });
        const ux = buildReviewUx(item.file.name, saved || item.result, {
          hardFailure: !saved?.extracted,
        });
        fail.push({
          file: item.file.name,
          displayName: formatImportDisplayName(
            item.file.name,
            (saved || item.result)?.extracted,
            saved?.invoiceNumber
          ),
          reason: ux.subtitle || friendly,
        });
      }
    });

    if (!opts?.silent && ok.length && onImported) onImported(ok);
    return { total: ok.length, analyzed: ok.length, imported: ok, needsReview: [], fail };
    } finally {
      confirmingRef.current = false;
      if (!opts?.silent) setIsBusy(false);
    }
  };

  const removeItem = (id: string) => setQueue((prev) => prev.filter((q) => q.id !== id));
  const clearDone = () =>
    setQueue((prev) => prev.filter((q) => q.status !== "imported" && q.status !== "error"));

  const queuedCount = queue.filter((q) => q.status === "queued").length;
  const readyCount = queue.filter((q) => q.status === "ready").length;
  const needsReviewCount = queue.filter((q) => q.status === "needs_review").length;
  const analyzedInRun = queue.filter(
    (q) =>
      runIdsRef.current.has(q.id) &&
      q.status !== "queued" &&
      q.status !== "analyzing"
  ).length;
  const progressPct =
    runTotal > 0 ? Math.min(100, Math.round((analyzedInRun / runTotal) * 100)) : 0;

  return (
    <div className="space-y-6">
      <div
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragActive(false);
          if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-all ${
          dragActive
            ? "border-violet-500 bg-violet-50/80 dark:bg-violet-950/30 scale-[1.01]"
            : "border-zinc-300 dark:border-zinc-700 bg-white/60 dark:bg-zinc-900/40 hover:border-violet-400 dark:hover:border-violet-600"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          multiple
          className="hidden"
          onChange={(e) => e.target.files && addFiles(e.target.files)}
        />
        <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-violet-500 mb-4" />
        <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
          Arrastra facturas (PDF, Excel, Word, CSV, imágenes…)
        </h3>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-md mx-auto">
          La IA extrae todas las líneas de concepto (servicios, gastos no suplidos y suplidos),
          cabecera fiscal, forma de pago y vencimiento desde cualquier formato habitual.
        </p>
        <p className="mt-3 text-xs text-zinc-400">
          PDF · JPG/PNG/WebP · XLS/XLSX · CSV · DOCX · TXT · ODT · Máx. 10 MB por archivo
        </p>
      </div>

      {(isBusy || batchMeta || retryMeta) && runTotal > 0 && (
        <div className="rounded-2xl border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20 p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-violet-800 dark:text-violet-200">
              {retryMeta
                ? `Reintentando ${retryMeta.current}/${retryMeta.total} — ${retryMeta.fileName}`
                : batchMeta?.pauseRemaining
                  ? `Lote ${batchMeta.current}/${batchMeta.total} — pausa ${batchMeta.pauseRemaining}s…`
                  : batchMeta
                    ? `Lote ${batchMeta.current}/${batchMeta.total} — procesando…`
                    : "Analizando documentos…"}
            </span>
            <span className="tabular-nums font-semibold text-violet-700 dark:text-violet-300">
              {progressPct}%
            </span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-violet-200/80 dark:bg-violet-900/50 overflow-hidden">
            <div
              className="h-full rounded-full bg-violet-600 transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-xs text-violet-600/90 dark:text-violet-400">
            {analyzedInRun} de {runTotal} analizados · lotes de {BATCH_SIZE}
          </p>
        </div>
      )}

      {queue.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          {queuedCount > 0 && (
            <button
              type="button"
              disabled={isBusy}
              onClick={processQueue}
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              <SparklesIcon className="h-5 w-5" />
              {isBusy
              ? batchAutoMode
                ? "Analizando e importando…"
                : "Analizando con IA…"
              : `Analizar con IA (${queuedCount})`}
            </button>
          )}
          <label className="inline-flex items-center gap-2 cursor-pointer select-none rounded-xl border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={batchAutoMode}
              disabled={isBusy}
              onChange={(e) => setBatchAutoMode(e.target.checked)}
              className="rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
            />
            Importación automática por lotes
          </label>
          {isBusy && queuedCount === 0 && batchAutoMode && (
            <p className="text-sm text-violet-700 dark:text-violet-300 flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 animate-pulse" />
              Procesando lotes…
            </p>
          )}
          {readyCount > 0 && (
            <button
              type="button"
              disabled={isBusy}
              onClick={() => confirmImport()}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircleIcon className="h-5 w-5" />
              Confirmar perfectas ({readyCount})
            </button>
          )}
          {needsReviewCount > 0 && (
            <button
              type="button"
              disabled={isBusy}
              onClick={() =>
                confirmImport(
                  queue
                    .filter((q) => q.status === "needs_review" && q.result?.extracted)
                    .map((item) => ({ item, result: item.result }))
                )
              }
              className="inline-flex items-center gap-2 rounded-xl border border-amber-400 px-4 py-2.5 text-sm font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50"
            >
              Importar tras revisión ({needsReviewCount})
            </button>
          )}
          {queue.some((q) => q.status === "imported" || q.status === "error") && (
            <button
              type="button"
              onClick={clearDone}
              className="rounded-xl border border-zinc-300 dark:border-zinc-600 px-4 py-2.5 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              Limpiar finalizados
            </button>
          )}
        </div>
      )}

      {queue.length > 0 && (
        <div className="flex flex-col lg:flex-row gap-6 min-h-[420px]">
          <div className="lg:w-80 shrink-0 space-y-2">
            {queue.map((item) => {
              const ext = item.result?.extracted;
              const conf = ext ? formatConfidencePercent(ext.confidence) : null;
              const fileMeta = getFileTypeMeta(item.file);
              const FileIcon = fileMeta.Icon;
              const selected = selectedId === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedId(item.id)}
                  className={`w-full text-left rounded-2xl border p-3 transition-all ${
                    selected
                      ? "border-violet-500 bg-violet-50/50 dark:bg-violet-950/20 shadow-sm"
                      : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600"
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileIcon className={`h-5 w-5 shrink-0 ${fileMeta.color}`} />
                    <span className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {item.file.name}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500">
                    <span>{statusLabels[item.status]}</span>
                    {conf != null && item.status === "ready" && (
                      <span className="tabular-nums text-violet-600 dark:text-violet-400">{conf}%</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <aside className="flex-1 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900/60 shadow-sm overflow-hidden">
            <AnimatePresence mode="wait">
              {selectedId && queue.find((q) => q.id === selectedId) ? (
                <motion.div
                  key={selectedId}
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="p-5 max-h-[70vh] overflow-y-auto"
                >
                  <QueueCard
                    item={queue.find((q) => q.id === selectedId)!}
                    variant="panel"
                    onRemove={() => {
                      removeItem(selectedId);
                      setSelectedId(null);
                    }}
                    onFieldChange={(field, value) => updateExtracted(selectedId, field, value)}
                    onLinesChange={(lines) => updateLines(selectedId, lines)}
                  />
                </motion.div>
              ) : (
                <motion.p
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-10 text-center text-sm text-zinc-500"
                >
                  Selecciona un documento para revisar la extracción OCR
                </motion.p>
              )}
            </AnimatePresence>
          </aside>
        </div>
      )}

      <AnimatePresence>
        {showSummary && runSummary && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setShowSummary(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl bg-white dark:bg-zinc-900 shadow-xl border border-zinc-200 dark:border-zinc-700 p-6 space-y-4"
            >
              <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                Resumen de importación
              </h3>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Analizados {runSummary.analyzed}/{runSummary.total} · {runSummary.imported.length}{" "}
                importadas · {runSummary.needsReview.length} revisar · {runSummary.fail.length} fallidos
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                {runSummary.imported.length > 0 && (
                  <p className="text-emerald-700 dark:text-emerald-400 font-medium">
                    {runSummary.imported.length} importada(s) con datos perfectos (≥95%)
                  </p>
                )}
                {runSummary.needsReview.length > 0 && (
                  <p className="text-amber-700 dark:text-amber-400 font-medium">
                    {runSummary.needsReview.length} para revisión manual (&lt;95% o datos dudosos)
                  </p>
                )}
                {runSummary.fail.length > 0 && (
                  <p className="text-red-600 dark:text-red-400 font-medium">
                    {runSummary.fail.length} fallidos
                  </p>
                )}
              </div>
              {runSummary.imported.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-zinc-500 mb-2">Números de factura</p>
                  <ul className="text-sm text-zinc-800 dark:text-zinc-200 space-y-1 max-h-32 overflow-y-auto">
                    {runSummary.imported.map((r, i) => (
                      <li key={`${r.file}-${i}`} className="flex items-center gap-2">
                        <CheckCircleIcon className="h-4 w-4 text-emerald-500 shrink-0" />
                        <span className="truncate">{r.invoiceNumber || r.file}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {runSummary.needsReview.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-2">
                    Revisa manualmente:
                  </p>
                  <ul className="text-sm text-amber-800 dark:text-amber-200 space-y-2 max-h-40 overflow-y-auto">
                    {runSummary.needsReview.map((r, i) => (
                      <li key={`${r.file}-rev-${i}`} className="flex gap-2">
                        <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                          <span className="font-medium">Revisa: {r.displayName}</span>
                          <span className="block text-xs opacity-90">{r.reason}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {runSummary.fail.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-2">
                    No se pudieron analizar:
                  </p>
                  <ul className="text-sm text-red-700 dark:text-red-300 space-y-2 max-h-40 overflow-y-auto">
                    {runSummary.fail.map((f, i) => (
                      <li key={`${f.file}-${i}`} className="flex gap-2">
                        <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>
                          <span className="font-medium">Revisa: {f.displayName}</span>
                          <span className="block text-xs opacity-90">{f.reason}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowSummary(false)}
                className="w-full rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-700"
              >
                Cerrar
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const statusLabels: Record<QueueStatus, string> = {
  queued: "En cola",
  analyzing: "Analizando…",
  ready: "Listo — importar",
  needs_review: "Revisar e importar",
  error: "No se pudo leer",
  imported: "Importada",
};

const QueueCard: React.FC<{
  item: QueueItem;
  variant?: "card" | "panel";
  onRemove: () => void;
  onFieldChange: (field: keyof PdfImportExtracted, value: unknown) => void;
  onLinesChange: (lines: EditableConceptLine[]) => void;
}> = ({ item, variant = "card", onRemove, onFieldChange, onLinesChange }) => {
  const ext = item.result?.extracted;
  const lines = item.editLines || (ext ? extractedToLines(ext) : []);
  const fiscal = ext ? computeFiscalPreview(ext) : null;
  const confidencePct = formatConfidencePercent(ext?.confidence);
  const fileMeta = getFileTypeMeta(item.file);
  const FileIcon = fileMeta.Icon;
  const fechaHasIssue =
    item.result?.fieldFailures?.includes("fecha") ||
    item.result?.validationErrors?.some((e) => e.field === "fecha") ||
    !!ext?._fechaMismatch ||
    !!ext?._fechaAmbiguous;
  const fechaEvidencia = ext?._fechaEvidencia?.filter(Boolean) ?? [];
  const fechaDisplayEs = ext?.fecha
    ? new Intl.DateTimeFormat("es-ES", { timeZone: "UTC" }).format(
        new Date(`${ext.fecha}T12:00:00Z`)
      )
    : null;

  const statusColors: Record<QueueStatus, string> = {
    queued: "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400",
    analyzing: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    ready: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
    needs_review: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    error: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    imported: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  };
  const patchLine = (lineId: string, patch: Partial<EditableConceptLine>) => {
    onLinesChange(lines.map((l) => (l.id === lineId ? { ...l, ...patch } : l)));
  };

  const addLine = (tipo: LineTipo = "servicio") => {
    onLinesChange([
      ...lines,
      {
        id: `new-${Date.now()}-${Math.random()}`,
        tipo,
        descripcion: "",
        importe: 0,
      },
    ]);
  };

  const removeLine = (lineId: string) => {
    onLinesChange(lines.filter((l) => l.id !== lineId));
  };

  const shellClass =
    variant === "panel"
      ? ""
      : "rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900/60 p-5 shadow-sm";

  return (
    <div className={shellClass}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <FileIcon className={`h-8 w-8 shrink-0 ${fileMeta.color}`} />
          <div className="min-w-0">
            <p className="font-medium text-zinc-900 dark:text-zinc-100 truncate">{item.file.name}</p>
            <p className="text-xs text-zinc-500">
              {fileMeta.label} · {(item.file.size / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[item.status]}`}>
            {statusLabels[item.status]}
          </span>
          <button type="button" onClick={onRemove} className="p-1 text-zinc-400 hover:text-red-500">
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {item.status === "analyzing" && (
        <ProcessingAnimation step={item.processingStep || "uploading"} />
      )}

      {(item.error && (item.status === "error" || item.status === "needs_review")) && (
        <div
          className={`flex items-start gap-2 rounded-lg p-3 text-sm ${
            item.status === "needs_review"
              ? "bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200"
              : "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300"
          }`}
        >
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            {(() => {
              const ux = buildReviewUx(item.file.name, item.result, {
                hardFailure: item.status === "error",
              });
              const title = ux.title;
              const subtitle = ux.subtitle || item.error?.replace(/^Revisa[^:]+:\s*—\s*/i, "") || item.error;
              return (
                <>
                  <p className="font-medium">{title}</p>
                  {subtitle && title !== subtitle && (
                    <p className="mt-0.5 text-xs opacity-90">{subtitle}</p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {ext && item.status !== "analyzing" && item.status !== "queued" && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <span className="text-xs px-2.5 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 font-medium tabular-nums">
              {confidencePct}% confianza
            </span>
            {(item.result?.needsReview || item.status === "needs_review") && (
              <span className="text-xs px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                {item.result?.validationStatus === "failed"
                  ? "Revisar datos"
                  : "Revisión manual"}
              </span>
            )}
            {item.result?.canAutoImport && item.status === "ready" && (
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700">
                Datos perfectos
              </span>
            )}
            {item.result?.documentType === "recibida" && (
              <span className="text-xs px-2 py-1 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700">
                Factura recibida (gasto)
              </span>
            )}
            {item.result?.invoiceNumber && (
              <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700">
                {item.result.invoiceNumber}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <Field label="Número" value={ext.numero || ""} onChange={(v) => onFieldChange("numero", v)} />
            <div className={fechaHasIssue ? "md:col-span-2" : undefined}>
              <Field
                label="Fecha de factura (emisión)"
                type="date"
                value={ext.fecha || ""}
                onChange={(v) => onFieldChange("fecha", v)}
                highlight={fechaHasIssue}
                hint={
                  fechaDisplayEs
                    ? `En documento (España): ${fechaDisplayEs}${
                        fechaEvidencia.length ? ` · «${fechaEvidencia[0]}»` : ""
                      }`
                    : fechaHasIssue
                      ? "Revise la fecha en el PDF (formato DD/MM/AAAA)"
                      : undefined
                }
              />
            </div>
            <Field label="Cliente" value={ext.cliente || ""} onChange={(v) => onFieldChange("cliente", v)} />
            <Field label="CIF/NIF" value={ext.cif || ""} onChange={(v) => onFieldChange("cif", v)} />
            <Field
              label="Forma de pago"
              value={ext.forma_pago || ""}
              onChange={(v) => onFieldChange("forma_pago", v)}
            />
            <Field
              label="Vencimiento"
              type="date"
              value={ext.vencimiento || ""}
              onChange={(v) => onFieldChange("vencimiento", v)}
            />
            <Field
              label="Base imponible"
              type="number"
              value={String(ext.base_imponible ?? "")}
              onChange={(v) => onFieldChange("base_imponible", parseFloat(v) || 0)}
            />
            <Field
              label="Total"
              type="number"
              value={String(ext.total ?? "")}
              onChange={(v) => onFieldChange("total", parseFloat(v) || 0)}
            />
            <Field
              label="IVA %"
              type="number"
              value={String(ext.iva_porcentaje ?? 21)}
              onChange={(v) => onFieldChange("iva_porcentaje", parseFloat(v) || 0)}
            />
            <Field
              label="IVA €"
              type="number"
              value={String(ext.iva_importe ?? "")}
              onChange={(v) => onFieldChange("iva_importe", parseFloat(v) || 0)}
            />
            <Field
              label="IRPF %"
              type="number"
              value={String(ext.irpf_porcentaje ?? 15)}
              onChange={(v) => onFieldChange("irpf_porcentaje", parseFloat(v) || 0)}
            />
            <Field
              label="IRPF €"
              type="number"
              value={String(ext.irpf_importe ?? "")}
              onChange={(v) => onFieldChange("irpf_importe", parseFloat(v) || 0)}
            />
          </div>

          <div className="border-t border-zinc-100 dark:border-zinc-800 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
                Conceptos / líneas
              </h4>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => addLine("servicio")}
                  className="inline-flex items-center gap-1 text-xs rounded-lg border border-zinc-300 dark:border-zinc-600 px-2 py-1 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                >
                  <PlusIcon className="h-3.5 w-3.5" /> Servicio
                </button>
                <button
                  type="button"
                  onClick={() => addLine("gasto_no_suplido")}
                  className="inline-flex items-center gap-1 text-xs rounded-lg border border-amber-300 px-2 py-1 text-amber-700 hover:bg-amber-50"
                >
                  <PlusIcon className="h-3.5 w-3.5" /> Gasto n.s.
                </button>
                <button
                  type="button"
                  onClick={() => addLine("suplido")}
                  className="inline-flex items-center gap-1 text-xs rounded-lg border border-blue-300 px-2 py-1 text-blue-700 hover:bg-blue-50"
                >
                  <PlusIcon className="h-3.5 w-3.5" /> Suplido
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {lines.length === 0 && (
                <p className="text-sm text-zinc-500 italic">
                  No hay líneas detectadas. Añade conceptos manualmente.
                </p>
              )}
              {lines.map((line) => (
                <div
                  key={line.id}
                  className={`rounded-xl border p-3 space-y-2 ${
                    line.tipo === "gasto_no_suplido"
                      ? "border-amber-200 bg-amber-50/50 dark:border-amber-900/50 dark:bg-amber-950/20"
                      : line.tipo === "suplido"
                        ? "border-blue-200 bg-blue-50/50 dark:border-blue-900/50 dark:bg-blue-950/20"
                        : "border-zinc-200 dark:border-zinc-700"
                  }`}
                >
                  <div className="flex flex-wrap gap-2 items-center">
                    <select
                      value={line.tipo}
                      onChange={(e) =>
                        patchLine(line.id, { tipo: e.target.value as LineTipo })
                      }
                      className="text-xs rounded-lg border border-zinc-300 dark:border-zinc-600 px-2 py-1 bg-white dark:bg-zinc-800"
                    >
                      {(Object.keys(TIPO_LABELS) as LineTipo[]).map((t) => (
                        <option key={t} value={t}>
                          {TIPO_LABELS[t]}
                        </option>
                      ))}
                    </select>
                    {line.tipo === "gasto_no_suplido" && (
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                        Gasto no suplido (con IVA/IRPF)
                      </span>
                    )}
                    {line.tipo === "suplido" && (
                      <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                        Suplido (sin IVA/IRPF)
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeLine(line.id)}
                      className="ml-auto p-1 text-zinc-400 hover:text-red-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <textarea
                    value={line.descripcion}
                    onChange={(e) => patchLine(line.id, { descripcion: e.target.value })}
                    rows={2}
                    placeholder="Descripción completa del concepto"
                    className="w-full rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={line.importe || ""}
                      onChange={(e) =>
                        patchLine(line.id, { importe: parseFloat(e.target.value) || 0 })
                      }
                      placeholder="Importe €"
                      className="w-32 rounded-lg border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-sm"
                    />
                    {line.tipo === "gasto_no_suplido" && (
                      <input
                        value={line.gastoTipo || "otros"}
                        onChange={(e) => patchLine(line.id, { gastoTipo: e.target.value })}
                        placeholder="Tipo gasto"
                        className="flex-1 rounded-lg border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-sm"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {fiscal && (
            <div className="rounded-xl bg-zinc-50 dark:bg-zinc-800/50 p-4 text-sm space-y-1">
              <p className="font-semibold text-zinc-700 dark:text-zinc-300 mb-2">
                Vista previa fiscal
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-zinc-600 dark:text-zinc-400">
                <span>Base trabajo:</span>
                <span className="text-right font-medium text-zinc-900 dark:text-zinc-100">
                  {formatCurrency(fiscal.baseTrabajo)}
                </span>
                <span>Base gastos n.s.:</span>
                <span className="text-right font-medium text-zinc-900 dark:text-zinc-100">
                  {formatCurrency(fiscal.baseGastos)}
                </span>
                <span>Base imponible:</span>
                <span className="text-right font-medium text-zinc-900 dark:text-zinc-100">
                  {formatCurrency(fiscal.baseImponible)}
                </span>
                <span>IVA ({fiscal.ivaPct}%):</span>
                <span className="text-right">{formatCurrency(fiscal.ivaImporte)}</span>
                <span>IRPF ({fiscal.irpfPct}%):</span>
                <span className="text-right">-{formatCurrency(fiscal.irpfImporte)}</span>
                <span>Suplidos:</span>
                <span className="text-right">{formatCurrency(fiscal.suplidos)}</span>
                <span className="font-semibold text-zinc-800 dark:text-zinc-200">Total:</span>
                <span className="text-right font-bold text-violet-700 dark:text-violet-300">
                  {formatCurrency(fiscal.total)}
                </span>
              </div>
            </div>
          )}

          {(ext.observaciones || ext.observacion) && (
            <p className="text-xs text-zinc-500 italic">{ext.observaciones || ext.observacion}</p>
          )}
        </div>
      )}

      {item.status === "imported" && item.result?.invoiceId && (
        <p className="mt-3 text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
          <CheckCircleIcon className="h-4 w-4" />
          Factura importada correctamente
        </p>
      )}
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  type?: string;
  onChange: (v: string) => void;
  highlight?: boolean;
  hint?: string;
}> = ({ label, value, type = "text", onChange, highlight, hint }) => (
  <div>
    <label
      className={`block text-xs font-medium mb-1 ${
        highlight ? "text-amber-700 dark:text-amber-300" : "text-zinc-500 dark:text-zinc-400"
      }`}
    >
      {label}
      {highlight && " ⚠"}
    </label>
    <input
      type={type}
      value={value}
      step={type === "number" ? "0.01" : undefined}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full rounded-lg border px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
        highlight
          ? "border-amber-400 dark:border-amber-600 bg-amber-50/80 dark:bg-amber-950/30"
          : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800"
      }`}
    />
    {hint && (
      <p
        className={`mt-1 text-xs ${
          highlight ? "text-amber-700 dark:text-amber-300" : "text-zinc-500 dark:text-zinc-400"
        }`}
      >
        {hint}
      </p>
    )}
  </div>
);

/** @deprecated Use ImportDocumentsDropzone */
export const ImportInvoicesDropzone = ImportDocumentsDropzone;
export type ImportInvoicesDropzoneProps = ImportDocumentsDropzoneProps;
